import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';

import {
  useEnrollmentProfileQuery,
  DecisionIQSurveySubmissionDto,
} from 'enrollment/queries';
import decIQLightbulb from 'enrollment/assets/DecIQLightbulb.svg';
import { InfoDrawer } from 'enrollment/features/DecisionSupportServices/DecisionIQ/components/DecisionIQInformation/InfoDrawer';
import { DecisionIQSurveyDrawer } from './DecisionIQSurveyDrawer';
import {
  RecommendationBannerStyled,
  IconWrapperStyled,
  LightbulbAnimationStyled,
  BannerBodyStyled,
  BannerSubTextStyled,
  InfoDrawerWrapperStyled,
  SurveyDrawerWrapperStyled,
} from './RecommendationBannerStyled';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { selectIsAdmin } from 'theSource/features';

interface RecommendationBannerProps {
  planName: string;
  hasSelectedDependents: boolean;
  orgBenefitId?: number;
  benefitLookup?: string;
  dependents?: DependentsResponseDto;
  onRecommendationSurveySubmit?: (arg0: DecisionIQSurveySubmissionDto) => void;
}

export const getSubscriberAge = (birthday: string | undefined) => {
  const birthDate = new Date(String(birthday));
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  return age;
};

export const RecommendationBanner = ({
  planName,
  hasSelectedDependents,
  orgBenefitId,
  benefitLookup,
  dependents,
  onRecommendationSurveySubmit,
}: RecommendationBannerProps) => {
  const { t } = useTranslation('pages.decisionIQ');
  const isAdmin = useSelector(selectIsAdmin);

  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
  const firstName = String(subscriber?.data?.first_name.value || '');
  const city = String(subscriber?.data?.city?.value || '');
  const birthdate = String(subscriber?.data?.birthdate?.value);
  const age = getSubscriberAge(birthdate) || '';

  const isLargeViewport = window.innerWidth > 743;
  const triggerLabelText = isLargeViewport
    ? t('plan_banner.personalize_lg')
    : t('plan_banner.personalize');
  return (
    <RecommendationBannerStyled
      className="banner decisioniq"
      role="alert"
      id="decisionIQ-recommendation-banner"
      data-testid="decisionIQ-recommendation-banner"
    >
      <IconWrapperStyled>
        <LightbulbAnimationStyled
          src={decIQLightbulb}
          alt="Decision IQ icon"
          className="mr-lg-3"
        />
      </IconWrapperStyled>
      <BannerBodyStyled>
        <Typography variant="body1">
          {t('plan_banner.text', {
            firstName: firstName,
            planName: planName,
          })}
        </Typography>
        {city !== 'undefined' && birthdate !== 'undefined' && (
          <BannerSubTextStyled variant="body2">
            {t('plan_banner.we_know_this', {
              age: age,
              city: city,
              familyMembers: hasSelectedDependents
                ? t('plan_banner.you_and_family')
                : t('plan_banner.yourself'),
            })}
          </BannerSubTextStyled>
        )}
        <InfoDrawerWrapperStyled>
          <InfoDrawer triggerLabel={t('plan_banner.find_out_how')} />
        </InfoDrawerWrapperStyled>
        {!isAdmin && (
          <SurveyDrawerWrapperStyled>
            <DecisionIQSurveyDrawer
              triggerLabel={triggerLabelText}
              subscriber={subscriber?.data}
              dependents={dependents}
              orgBenefitId={orgBenefitId}
              benefitLookup={benefitLookup}
              onRecommendationSurveySubmit={onRecommendationSurveySubmit}
            />
          </SurveyDrawerWrapperStyled>
        )}
      </BannerBodyStyled>
    </RecommendationBannerStyled>
  );
};
